import { useNavigate } from "react-router-dom";
import { Heart, Trash2, Music2, Loader2, AlertTriangle, Play, Check } from "lucide-react";
import { fmtTime } from "@/lib/music";
import { DemoBadge } from "@/components/Badges";

export default function SongCard({ song, onFavorite, onDelete, selectable, selected, onSelect }) {
  const nav = useNavigate();
  const isReady = song.status === "completed";
  const isFailed = song.status === "failed";

  const open = () => {
    if (selectable) {
      if (isReady) onSelect(song);
      return;
    }
    if (isReady) nav(`/studio/${song.id}`);
    else nav(`/processing/${song.id}`);
  };

  return (
    <div
      data-testid={`song-card-${song.id}`}
      className={`group relative flex items-center gap-4 rounded-xl border bg-[#131822] p-4 transition-colors ${
        selectable && selected ? "border-cyan-400/70 ring-1 ring-cyan-400/30" : "border-[#222A38] hover:border-[#3A4659]"
      }`}
    >
      <button
        onClick={open}
        disabled={selectable && !isReady}
        data-testid={`open-song-${song.id}`}
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#1A202C] to-[#0B0E14] text-cyan-400 ring-1 ring-[#222A38] transition-transform group-hover:scale-105 disabled:opacity-40"
      >
        {selectable ? (
          selected ? <Check size={24} className="text-cyan-400" /> : <span className="h-5 w-5 rounded-full border-2 border-slate-500" />
        ) : isReady ? <Play size={22} className="fill-cyan-400" /> : isFailed ? <AlertTriangle size={22} className="text-red-400" /> : <Loader2 size={22} className="animate-spin" />}
      </button>

      <button onClick={open} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-semibold text-white">{song.title}</h3>
          {song.analysis?.is_demo && <DemoBadge />}
        </div>
        <p className="truncate text-sm text-slate-400">{song.artist || "Unknown artist"}</p>
        <div className="mt-1 flex items-center gap-3 font-mono-sw text-[11px] text-slate-500">
          {song.duration > 0 && <span className="inline-flex items-center gap-1"><Music2 size={11} />{fmtTime(song.duration)}</span>}
          {song.analysis?.key?.value && <span>{song.analysis.key.value}</span>}
          {song.analysis?.bpm?.value && <span>{song.analysis.bpm.value} BPM</span>}
          {!isReady && !isFailed && <span className="text-cyan-400">Processing…</span>}
          {isFailed && <span className="text-red-400">Failed — tap to retry</span>}
        </div>
      </button>

      <div className="flex flex-col items-center gap-2">
        {!selectable && onFavorite && (
          <button
            data-testid={`favorite-song-${song.id}`}
            onClick={() => onFavorite(song)}
            className="rounded-full p-1.5 transition-colors hover:bg-white/5"
          >
            <Heart size={18} className={song.favorite ? "fill-rose-500 text-rose-500" : "text-slate-500"} />
          </button>
        )}
        {!selectable && onDelete && (
          <button
            data-testid={`delete-song-${song.id}`}
            onClick={() => onDelete(song)}
            className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
          >
            <Trash2 size={16} />
          </button>
        )}
        {selectable && !isReady && (
          <span className="font-mono-sw text-[10px] text-slate-600">not ready</span>
        )}
      </div>
    </div>
  );
}
